import { useCallback, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { ROUTE_URLS } from 'router/routes'

import { shortLanguages } from 'shared/locale'
import { useMediaMaxTablet } from 'shared/style/var'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { actions } from 'features/slots'
import {
  selectBackUrl,
  selectDigitainSlotToken,
  selectGameList,
  selectGameListLoading,
  selectGameUrl,
  selectSessionUrlComm,
} from 'features/slots/selectors'
import { selectLanguage } from 'features/userSettings/selectors'

import { addNotify } from 'components/Notify'

import { getPureGameId } from '../providersData/GameCatalog/uniqGameId'
import { Providers, ProvidersType } from '../types/configuration'
import { createDigitainUrl } from '../utils/createDigitainUrl'

type ISlotFrameRouteMatch = {
  provider: ProvidersType
  gameId: string
  demoStart?: string
}

export const useSlotSession = () => {
  const dispatch = useDispatch()
  const { replace, push } = useHistory()
  const isMobileOrTablet = useMediaMaxTablet()
  const match = useRouteMatch<ISlotFrameRouteMatch>()

  const { provider, gameId, demoStart } = match.params

  const gameList = useSelector(selectGameList, shallowEqual)
  const isGameListLoading = useSelector(selectGameListLoading, shallowEqual)
  const _sessionUrl = useSelector(selectGameUrl, shallowEqual)
  const sessionUrlComm = useSelector(selectSessionUrlComm, shallowEqual)
  const digitainSlotToken = useSelector(selectDigitainSlotToken, shallowEqual)
  const backUrl = useSelector(selectBackUrl, shallowEqual)
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const lang = useSelector(selectLanguage, shallowEqual)

  const isLoading = useMemo(() => Object.values(sessionUrlComm).some(t => t?.isLoading), [sessionUrlComm])

  const callback = useCallback(() => {
    replace(ROUTE_URLS.SLOTS_CASINO())
    addNotify(locale.tryVpn)
  }, [replace, locale])

  const isDigitainProvider = useMemo(() => provider === Providers.digitain, [provider])

  console.log(isDigitainProvider)

  const digitanSlotUrl = createDigitainUrl({
    gameId,
    token: digitainSlotToken,
    deviceType: isMobileOrTablet ? '2' : '1',
    lang: shortLanguages[lang],
    playMode: demoStart ? 'demo' : undefined,
  })

  const providerGameList = useMemo(() => gameList.filter(g => g.routeProvider === provider), [provider, gameList])

  const hasFreeSpins = useMemo(
    () => providerGameList.find(slot => slot.gameId === gameId)?.hasFreeSpins,
    [gameId, providerGameList],
  )

  useEffect(() => {
    if (isAuth || demoStart) {
      if (!providerGameList.length) {
        dispatch(actions.getGameList({ id: provider, provider, platform: isMobileOrTablet ? 'mobile' : 'desktop' }))
      }
    } else {
      addNotify(locale.pleaseAuth)
    }
  }, [])

  useEffect(() => {
    if (providerGameList.length && !isDigitainProvider) {
      dispatch(
        actions.getSessionUrl({
          id: gameId,
          provider,
          gameId,
          isDesktop: !isMobileOrTablet,
          callback,
          demoStart,
          hasFreeSpins,
          withRedirect: isMobileOrTablet,
        }),
      )
    }

    return () => {
      dispatch(actions.clearSessionUrl())
    }
  }, [gameId, provider, providerGameList])

  useEffect(() => {
    if (isDigitainProvider && digitanSlotUrl && isMobileOrTablet) {
      replace(backUrl || ROUTE_URLS.SLOTS_CASINO())
      setTimeout(() => {
        location.href = digitanSlotUrl
      }, 0)
    }

    if (!isAuth) {
      callback()
    }
  }, [isDigitainProvider, digitanSlotUrl])

  const sessionUrl = useMemo(
    () => (isDigitainProvider ? digitanSlotUrl : _sessionUrl),
    [isDigitainProvider, digitanSlotUrl, _sessionUrl],
  )

  return {
    sessionUrl,
    demoStart,
    isLoading,
    isGameListLoading,
    isDigitainProvider,
  }
}
